import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4011';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

export interface UserProfile {
  id: string;
  email: string;
  isEmailVerified: boolean;
  isPremium: boolean;
  isAdmin: boolean;
  banned: boolean;
  createdAt: string;
  updatedAt: string;
  preferences?: UserPreferences;
  stats?: UserStats;
}

export interface UserPreferences {
  id: string;
  userId: string;
  defaultVideoQuality: string;
  defaultAudioEnabled: boolean;
  defaultVideoEnabled: boolean;
  preferredCategories: string[];
  autoConnectAfterRating: boolean;
  enableIPProtection: boolean;
  emailNotifications: boolean;
  marketingEmails: boolean;
  showOnlineStatus: boolean;
  allowDataCollection: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UserStats {
  id: string;
  userId: string;
  totalSessions: number;
  totalMinutes: number;
  sessionsToday: number;
  lastSessionDate: string | null;
  averageRating: number | null;
  totalReportsReceived: number;
  totalReportsMade: number;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateProfileDto {
  email?: string;
  displayName?: string;
}

export interface ChangePasswordDto {
  currentPassword: string;
  newPassword: string;
}

export interface UpdatePreferencesDto {
  defaultVideoQuality?: string;
  defaultAudioEnabled?: boolean;
  defaultVideoEnabled?: boolean;
  preferredCategories?: string[];
  autoConnectAfterRating?: boolean;
  enableIPProtection?: boolean;
  emailNotifications?: boolean;
  marketingEmails?: boolean;
  showOnlineStatus?: boolean;
  allowDataCollection?: boolean;
}

export const usersService = {
  /**
   * Get current user profile
   */
  async getProfile(): Promise<UserProfile> {
    const { data } = await apiClient.get<UserProfile>('/api/users/me');
    return data;
  },

  /**
   * Update user profile
   */
  async updateProfile(updateDto: UpdateProfileDto): Promise<UserProfile> {
    const { data } = await apiClient.patch<UserProfile>('/api/users/me', updateDto);
    return data;
  },

  /**
   * Change password
   */
  async changePassword(changeDto: ChangePasswordDto): Promise<{ message: string }> {
    const { data } = await apiClient.patch<{ message: string }>(
      '/api/users/me/password',
      changeDto,
    );
    return data;
  },

  /**
   * Get user preferences
   */
  async getPreferences(): Promise<UserPreferences> {
    const { data } = await apiClient.get<UserPreferences>('/api/users/me/preferences');
    return data;
  },

  /**
   * Update user preferences
   */
  async updatePreferences(updateDto: UpdatePreferencesDto): Promise<UserPreferences> {
    const { data } = await apiClient.patch<UserPreferences>(
      '/api/users/me/preferences',
      updateDto,
    );
    return data;
  },

  /**
   * Delete user account
   */
  async deleteAccount(password: string): Promise<{ message: string }> {
    const { data } = await apiClient.delete<{ message: string }>('/api/users/me', {
      data: { password },
    });
    return data;
  },

  /**
   * Export user data (GDPR)
   */
  async exportData(): Promise<any> {
    const { data } = await apiClient.get('/api/users/me/export');
    return data;
  },
};

export default usersService;
